// Client-side cost projection used by the wizard preview before a campaign
// is saved to the backend. The backend has the same logic in
// services/costEstimate.ts; the two stay in sync because the projection
// rules are simple enough that duplication is cheaper than another API
// round-trip. If you change one, change the other.

import type { CostEstimate, RewardInput, RewardProjection } from './types';

export interface DraftCampaign {
  luckyDrawRatio: number;
  startsAt: string;
  endsAt: string;
}

export interface ProjectionParams {
  targetParticipants: number;
  milestoneRates?: Record<string, number>;
  campaignDays?: number;
}

function realCost(r: RewardInput): number {
  return r.rewardRealCostEur != null ? r.rewardRealCostEur : r.rewardValueEur;
}

function projectReward(
  r: RewardInput,
  campaign: DraftCampaign,
  targetParticipants: number,
  milestoneRates: Record<string, number>,
  campaignDays: number,
): RewardProjection {
  const cost = realCost(r);
  const base = {
    rewardId: '',
    rewardLabel: r.rewardLabel,
    triggerType: r.triggerType as string,
    triggerValue: r.triggerValue ?? null,
    isCumulative: r.isCumulative ?? true,
    realCostEur: cost,
    maxWinners: r.maxWinners ?? null,
    cappedByMax: false,
  };

  switch (r.triggerType) {
    case 'lucky_draw': {
      const ratio = r.triggerLuckyDrawRatio ?? campaign.luckyDrawRatio ?? 20;
      const winners = ratio > 0 ? targetParticipants / ratio : 0;
      return { ...base, projectedWinners: winners, projectedCost: winners * cost };
    }
    case 'referral_milestone': {
      const rate = milestoneRates[String(r.triggerValue)] ?? 0;
      const winners = targetParticipants * rate;
      return { ...base, projectedWinners: winners, projectedCost: winners * cost };
    }
    case 'top_n_overall': {
      const winners = Math.min(r.triggerValue ?? 0, targetParticipants);
      return { ...base, projectedWinners: winners, projectedCost: winners * cost };
    }
    // Periodic top-N rewards always pay triggerValue per period; isCumulative
    // only affects whether the same person can win twice across periods, not
    // the number of awards. 2-week campaign × weekly top 3 = 6 awards always.
    case 'top_n_daily': {
      const periods = Math.max(1, campaignDays);
      const winners = (r.triggerValue ?? 0) * periods;
      return {
        ...base,
        periodsInCampaign: periods,
        projectedWinners: winners,
        projectedCost: winners * cost,
      };
    }
    case 'top_n_weekly': {
      const periods = Math.max(1, Math.ceil(campaignDays / 7));
      const winners = (r.triggerValue ?? 0) * periods;
      return {
        ...base,
        periodsInCampaign: periods,
        projectedWinners: winners,
        projectedCost: winners * cost,
      };
    }
    case 'top_n_monthly': {
      const periods = Math.max(1, Math.ceil(campaignDays / 30));
      const winners = (r.triggerValue ?? 0) * periods;
      return {
        ...base,
        periodsInCampaign: periods,
        projectedWinners: winners,
        projectedCost: winners * cost,
      };
    }
    default:
      return { ...base, projectedWinners: 0, projectedCost: 0 };
  }
}

// Compute one projection for a given participant count. Used both for the
// main "expected" projection and for the conservative/optimistic scenarios
// — recomputing per scenario so maxWinners caps and top-N fixed counts are
// reflected accurately (multiplying the expected total breaks both).
//
// Each milestone reward is counted independently: a participant who hits
// 5 referrals also won the 3-referral reward earlier, so both costs apply.
// The slider invariant rate(lower) ≥ rate(higher) is enforced in the UI.
function computeProjection(
  campaign: DraftCampaign,
  rewards: RewardInput[],
  targetParticipants: number,
  milestoneRates: Record<string, number>,
  days: number,
) {
  const projections = rewards.map((r) =>
    projectReward(r, campaign, targetParticipants, milestoneRates, days),
  );

  for (const p of projections) {
    if (p.maxWinners != null && p.projectedWinners > p.maxWinners) {
      p.projectedWinners = p.maxWinners;
      p.projectedCost = p.maxWinners * p.realCostEur;
      p.cappedByMax = true;
    }
  }

  const totalCost = projections.reduce((s, p) => s + p.projectedCost, 0);
  const costPerParticipant = targetParticipants > 0 ? totalCost / targetParticipants : 0;
  return { projections, totalCost, costPerParticipant };
}

export function projectDraftCampaign(
  campaign: DraftCampaign,
  rewards: RewardInput[],
  params: ProjectionParams,
): CostEstimate {
  const milestoneRates = params.milestoneRates ?? {};
  const days =
    params.campaignDays ??
    Math.max(
      1,
      Math.ceil(
        (new Date(campaign.endsAt).getTime() - new Date(campaign.startsAt).getTime()) /
          (24 * 3600 * 1000),
      ),
    );

  const expected = computeProjection(
    campaign,
    rewards,
    params.targetParticipants,
    milestoneRates,
    days,
  );
  const conservativeTarget = Math.round(params.targetParticipants * 0.5);
  const optimisticTarget = params.targetParticipants * 2;
  const conservative = computeProjection(
    campaign,
    rewards,
    conservativeTarget,
    milestoneRates,
    days,
  );
  const optimistic = computeProjection(
    campaign,
    rewards,
    optimisticTarget,
    milestoneRates,
    days,
  );

  const round = (n: number) => Number(n.toFixed(2));

  return {
    targetParticipants: params.targetParticipants,
    assumptions: {
      luckyDrawRatio: campaign.luckyDrawRatio,
      milestoneRates,
      campaignDays: days,
    },
    rewardProjections: expected.projections.map((p) => ({
      ...p,
      projectedWinners: round(p.projectedWinners),
      projectedCost: round(p.projectedCost),
    })),
    summary: {
      totalProjectedCost: round(expected.totalCost),
      costPerParticipant: round(expected.costPerParticipant),
      currency: 'EUR',
      scenarios: {
        conservative: {
          participants: conservativeTarget,
          totalCost: round(conservative.totalCost),
          costPerParticipant: round(conservative.costPerParticipant),
        },
        expected: {
          participants: params.targetParticipants,
          totalCost: round(expected.totalCost),
          costPerParticipant: round(expected.costPerParticipant),
        },
        optimistic: {
          participants: optimisticTarget,
          totalCost: round(optimistic.totalCost),
          costPerParticipant: round(optimistic.costPerParticipant),
        },
      },
    },
  };
}
